import { memo, ReactNode, useEffect, useRef, useState } from "react";
import { Input, Select } from "antd";
import {
	AlertTriangle,
	CheckCircle2,
	Eye,
	MessageSquare,
	Pencil,
	ShieldCheck,
	ThumbsUp,
	Trash2,
	X
} from "lucide-react";

import {
	PlanReviewFeedbackDecision,
	PlanReviewFeedbackErrorAttribution,
	PlanReviewFeedbackReason,
	isAuthorityRole
} from "constants/field-values";
import { PLAN_REVIEW_FEEDBACK_REASON_OPTIONS } from "constants/constants";
import { feedbackReasonLabel } from "utils/plan-review-finding-review";

/** One plan_review_feedback row, as it arrives nested under the finding. */
export interface PlanReviewFeedbackRow {
	id: string;
	decision?: string | null;
	error_attribution?: string | null;
	reason?: string | null;
	comment?: string | null;
	user_id: string;
	/** Role of the author when the row was saved. Authority roles mean reviewer feedback. */
	user_role: string;
	user_name?: string | null;
	organization_name?: string | null;
	created_at: string;
	updated_at: string;
}

export interface PlanReviewFeedbackSubmission {
	decision: PlanReviewFeedbackDecision;
	errorAttribution: PlanReviewFeedbackErrorAttribution | null;
	reason: PlanReviewFeedbackReason | null;
	comment: string;
	/** Set when the user is editing their own saved row instead of writing a new one. */
	editingFeedbackId: string | null;
}

/** A decision that the modal asks the panel to open with, for example Incorrect from the "Dismiss" button. */
export interface PlanReviewFeedbackDecisionRequest {
	decision: PlanReviewFeedbackDecision;
	/** Changes on every request, so the same decision can be requested twice. */
	requestId: number;
}

export interface PlanReviewFeedbackSectionProps {
	/** Panel title, for example "Reviewer Feedback" or "Applicant Feedback". */
	title: string;
	/** Text under the title. */
	subtitle: string;
	/** Every feedback row that the user can see, newest first. Each row is shown as one card. */
	feedbacks: PlanReviewFeedbackRow[];
	/** The latest row of the user's group. While it exists, the form is closed until the user edits a row. */
	savedFeedback: PlanReviewFeedbackRow | null;
	/** Id of the logged user. Only the author of a row can edit or delete it. */
	currentUserId: string | null;
	/** False on older submittals: the cards are read-only and the form is not shown. */
	canWriteFeedback: boolean;
	submitting: boolean;
	deleting: boolean;
	/** Reviewers say which part of the analysis was off. Applicants pick a reason instead. */
	asksWhichPartWasOff: boolean;
	/** Applicants must pick a reason when the rating is Partially Correct or Incorrect. */
	requiresReason: boolean;
	/** Label of the submit button when the rating is Incorrect, for example "Save Feedback and move to Dismissed". */
	incorrectSubmitLabel?: string;
	/** Text shown at the top of the form when the rating is Incorrect, for example to say where the finding moves. */
	incorrectNotice?: ReactNode;
	/** Opens the form with this decision selected and scrolls the panel into view. */
	decisionRequest?: PlanReviewFeedbackDecisionRequest | null;
	/** Resolves true when the row was persisted, which clears the form. */
	onSubmit: (submission: PlanReviewFeedbackSubmission) => Promise<boolean>;
	/** Deletes the feedback row with this id. Resolves true when the row was removed. */
	onDelete: (feedbackId: string) => Promise<boolean>;
}

// Three-state rating. One decision + a disambiguation follow-up.
const DECISION_OPTIONS: {
	value: PlanReviewFeedbackDecision;
	label: string;
	icon: typeof CheckCircle2;
}[] = [
	{ value: PlanReviewFeedbackDecision.CONFIRMED, label: "Confirmed Accurate", icon: ThumbsUp },
	{ value: PlanReviewFeedbackDecision.PARTIALLY_CORRECT, label: "Partially Correct", icon: Pencil },
	{ value: PlanReviewFeedbackDecision.INCORRECT, label: "Incorrect", icon: X }
];

// Disambiguation: what the reviewer picks becomes error_attribution, which is what makes
// per-model accuracy queryable (reading the plans vs. applying the requirement).
const ATTRIBUTION_OPTIONS: { value: PlanReviewFeedbackErrorAttribution; label: string; icon: typeof CheckCircle2 }[] = [
	{ value: PlanReviewFeedbackErrorAttribution.PERCEPTION, label: "Observation was wrong", icon: Eye },
	{ value: PlanReviewFeedbackErrorAttribution.REASONING, label: "Compliance call was wrong", icon: ShieldCheck },
	{ value: PlanReviewFeedbackErrorAttribution.BOTH, label: "Both", icon: AlertTriangle }
];

const humanize = (raw?: string | null): string =>
	raw
		? raw
				.replace(/_/g, " ")
				.toLowerCase()
				.replace(/\b\w/g, c => c.toUpperCase())
		: "";

const formatDate = (iso?: string | null): string => (iso ? new Date(iso).toLocaleString() : "—");

const decisionLabel = (decision?: string | null): string | null =>
	decision ? (DECISION_OPTIONS.find(option => option.value === decision)?.label ?? humanize(decision)) : null;

const attributionLabel = (attribution?: string | null): string | null =>
	attribution
		? (ATTRIBUTION_OPTIONS.find(option => option.value === attribution)?.label ?? humanize(attribution))
		: null;

const decisionTagClass = (decision?: string | null): string => {
	if (decision === PlanReviewFeedbackDecision.CONFIRMED) return "prfs-history-tag-up";
	if (decision === PlanReviewFeedbackDecision.PARTIALLY_CORRECT) return "prfs-history-tag-warn";
	return "prfs-history-tag-down";
};

// Icon + colour for the saved attribution chip. One subsystem off (perception OR
// reasoning) = warning amber; both off = fail red.
const attributionVisual = (attribution?: string | null): { icon: typeof CheckCircle2; cls: string } => {
	switch (attribution) {
		case PlanReviewFeedbackErrorAttribution.PERCEPTION:
			return { icon: Eye, cls: "prfs-history-attr-warning" };
		case PlanReviewFeedbackErrorAttribution.REASONING:
			return { icon: ShieldCheck, cls: "prfs-history-attr-warning" };
		case PlanReviewFeedbackErrorAttribution.BOTH:
			return { icon: AlertTriangle, cls: "prfs-history-attr-fail" };
		default:
			return { icon: CheckCircle2, cls: "prfs-history-attr-pass" };
	}
};

const feedbackAuthorRole = (feedback: PlanReviewFeedbackRow): string =>
	isAuthorityRole(feedback.user_role) ? "Reviewer" : "Applicant";

/**
 * One saved feedback row: author, role, rating badges, date and comment. The author of the row gets
 * the edit and delete icon buttons. While the row is being edited, editForm replaces the badges and
 * the comment.
 */
const FeedbackCard = ({
	feedback,
	canChange,
	deleting,
	editForm,
	onEdit,
	onDelete
}: {
	feedback: PlanReviewFeedbackRow;
	canChange: boolean;
	deleting: boolean;
	/** The open form when this row is being edited, otherwise null. */
	editForm: ReactNode;
	onEdit: () => void;
	onDelete: () => void;
}) => {
	const attribution = feedback.error_attribution ?? null;
	const attr = attributionLabel(attribution);
	const attrVisual = attributionVisual(attribution);
	const AttrIcon = attrVisual.icon;
	const savedReasonLabel = feedbackReasonLabel(feedback.reason);
	const authorRole = feedbackAuthorRole(feedback);

	return (
		<div className="prfs-saved">
			<div className="prfs-saved-author-row">
				<span className="prfs-saved-author">
					<span>
						{feedback.user_name || authorRole}
						{feedback.organization_name && (
							<span className="prfs-saved-organization"> ({feedback.organization_name})</span>
						)}
					</span>
					<span className="prfs-saved-role">{authorRole}</span>
				</span>
				{canChange && !editForm && (
					<span className="prfs-saved-actions">
						<button
							type="button"
							className="prfs-cancel-button prfs-icon-button"
							onClick={onEdit}
							title="Edit feedback"
							aria-label="Edit feedback"
						>
							<Pencil size={14} />
						</button>
						<button
							type="button"
							className="prfs-delete-button prfs-icon-button"
							onClick={onDelete}
							disabled={deleting}
							title="Delete feedback"
							aria-label="Delete feedback"
						>
							<Trash2 size={14} />
						</button>
					</span>
				)}
			</div>
			{editForm ?? (
				<>
					<div className="prfs-history-head">
						<span className={`prfs-history-tag ${decisionTagClass(feedback.decision)}`}>
							{decisionLabel(feedback.decision) ?? "Feedback"}
						</span>
						{attr && (
							<span className={`prfs-history-attr ${attrVisual.cls}`}>
								<AttrIcon className="prfs-history-attr-icon" />
								{attr}
							</span>
						)}
						{savedReasonLabel && <span className="prfs-history-reason">{savedReasonLabel}</span>}
						{feedback.updated_at && <p>{formatDate(feedback.updated_at)}</p>}
					</div>
					{feedback.comment && <p className="prfs-history-comment">{feedback.comment}</p>}
				</>
			)}
		</div>
	);
};

/**
 * Feedback panel inside the plan review finding modal ("Reviewer Feedback" for authority
 * roles, "Applicant Feedback" for the other roles): a three-state decision plus, on the
 * non-confirming paths, which part of the analysis was off (reviewers), a reason from a fixed
 * list and a comment.
 *
 * The panel lists every feedback row that the user can see as a card, newest first. A new rating
 * writes a new row, so the finding keeps the full history of feedback. The author of a row can edit
 * it in place or delete it with the icon buttons of its card.
 *
 * Owns only the form; the modal owns the mutations. Form state is per-mount, so the modal
 * re-keys the panel per finding to reset it.
 */
const PlanReviewFeedbackSection = ({
	title,
	subtitle,
	feedbacks,
	savedFeedback,
	currentUserId,
	canWriteFeedback,
	submitting,
	deleting,
	asksWhichPartWasOff,
	requiresReason,
	incorrectSubmitLabel,
	incorrectNotice,
	decisionRequest,
	onSubmit,
	onDelete
}: PlanReviewFeedbackSectionProps) => {
	const sectionRef = useRef<HTMLDivElement>(null);
	const [decision, setDecision] = useState<PlanReviewFeedbackDecision | null>(null);
	const [errorAttribution, setErrorAttribution] = useState<PlanReviewFeedbackErrorAttribution | null>(null);
	const [reason, setReason] = useState<PlanReviewFeedbackReason | null>(null);
	const [comment, setComment] = useState("");
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [isEditing, setIsEditing] = useState(false);
	// Id of the row that the open form changes ("Edit"). Null when the form writes a new row.
	const [editingFeedbackId, setEditingFeedbackId] = useState<string | null>(null);
	// True only after a successful save in THIS panel — drives the ephemeral "Feedback
	// received" banner, so it never shows just because saved feedback already exists.
	const [justSubmitted, setJustSubmitted] = useState(false);

	const resetForm = () => {
		setDecision(null);
		setErrorAttribution(null);
		setReason(null);
		setComment("");
		setSubmitError(null);
		setIsEditing(false);
		setEditingFeedbackId(null);
	};

	// Opens the form with the requested decision, for example when the applicant clicks
	// "Dismiss and Leave Feedback" at the top of the modal.
	useEffect(() => {
		if (!decisionRequest) return;
		setDecision(decisionRequest.decision);
		setErrorAttribution(null);
		setSubmitError(null);
		setIsEditing(true);
		setEditingFeedbackId(null);
		sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
	}, [decisionRequest]);

	const fillFormWithFeedback = (feedback: PlanReviewFeedbackRow) => {
		setDecision((feedback.decision as PlanReviewFeedbackDecision) ?? null);
		setErrorAttribution((feedback.error_attribution as PlanReviewFeedbackErrorAttribution) ?? null);
		setReason((feedback.reason as PlanReviewFeedbackReason) ?? null);
		setComment(feedback.comment ?? "");
		setSubmitError(null);
		setIsEditing(true);
	};

	// Opens the form pre-filled with one of the user's own rows. Saving it changes that row.
	const handleStartEdit = (feedback: PlanReviewFeedbackRow) => {
		fillFormWithFeedback(feedback);
		setEditingFeedbackId(feedback.id);
		setJustSubmitted(false);
	};

	const handleDelete = async (feedbackId: string) => {
		const removed = await onDelete(feedbackId);
		if (removed && feedbackId === editingFeedbackId) resetForm();
	};

	const handleSubmit = async () => {
		if (decision === null) return;
		const isNotConfirmed = decision !== PlanReviewFeedbackDecision.CONFIRMED;
		if (isNotConfirmed && requiresReason && reason === null) {
			setSubmitError("Please select a reason when rating a finding as Partially Correct or Incorrect.");
			return;
		}
		// On the non-confirming paths, the reviewer must either pick which part was off or
		// explain in a comment (the "can't pick a clean attribution" escape hatch).
		const hasNoExplanation = errorAttribution === null && reason === null && comment.trim().length === 0;
		if (isNotConfirmed && asksWhichPartWasOff && hasNoExplanation) {
			setSubmitError("Tell Ollie which part was off, or add a short comment.");
			return;
		}
		setSubmitError(null);

		const saved = await onSubmit({
			decision,
			errorAttribution: isNotConfirmed && asksWhichPartWasOff ? errorAttribution : null,
			reason: isNotConfirmed ? reason : null,
			comment: comment.trim(),
			editingFeedbackId
		});
		if (!saved) return;

		resetForm();
		setJustSubmitted(true);
	};

	const selectSubmitLabel = (): string => {
		if (editingFeedbackId) return "Save changes";
		if (decision === PlanReviewFeedbackDecision.INCORRECT && incorrectSubmitLabel) return incorrectSubmitLabel;
		return "Submit feedback";
	};
	const submitLabel = selectSubmitLabel();

	// The rating buttons and the form. Shown inside the card being edited, or under the cards for a new rating.
	const feedbackForm = (
		<>
			<div className="prfs-decision-row">
				{DECISION_OPTIONS.map(option => {
					const DecisionIcon = option.icon;
					const isActive = decision === option.value;
					return (
						<button
							key={option.value}
							type="button"
							className={`prfs-decision-btn ${isActive ? "prfs-decision-btn-active" : ""}`}
							onClick={() => {
								setDecision(option.value);
								if (option.value === PlanReviewFeedbackDecision.CONFIRMED) setErrorAttribution(null);
								setSubmitError(null);
							}}
						>
							<DecisionIcon className="prfs-decision-btn-icon" />
							{option.label}
						</button>
					);
				})}
			</div>

			{decision && (
				<div className="prfs-form">
					{decision === PlanReviewFeedbackDecision.INCORRECT && incorrectNotice && (
						<p className="prfs-notice">{incorrectNotice}</p>
					)}

					{/* Disambiguation + comment — only on the two non-confirming paths. */}
					{decision !== PlanReviewFeedbackDecision.CONFIRMED && (
						<>
							{asksWhichPartWasOff && (
								<div className="prfs-attribution">
									<p className="prfs-attribution-label">Which part was off?</p>
									<div className="prfs-attribution-options">
										{ATTRIBUTION_OPTIONS.map(option => {
											const AttributionIcon = option.icon;
											const isActive = errorAttribution === option.value;
											return (
												<button
													key={option.value}
													type="button"
													className={`prfs-attribution-btn ${isActive ? "prfs-attribution-btn-active" : ""}`}
													onClick={() => {
														setErrorAttribution(isActive ? null : option.value);
														setSubmitError(null);
													}}
												>
													<AttributionIcon className="prfs-attribution-btn-icon" />
													{option.label}
												</button>
											);
										})}
									</div>
								</div>
							)}

							<div className="prfs-reason">
								<p className="prfs-attribution-label">{requiresReason ? "Reason (required)" : "Reason"}</p>
								<Select
									className="prfs-reason-select"
									placeholder="Select a reason…"
									value={reason ?? undefined}
									status={submitError && requiresReason && reason === null ? "error" : undefined}
									options={PLAN_REVIEW_FEEDBACK_REASON_OPTIONS.map(option => ({
										value: option.value,
										label: option.label
									}))}
									onChange={(value: PlanReviewFeedbackReason) => {
										setReason(value);
										setSubmitError(null);
									}}
								/>
							</div>

							<div className="prfs-comment-field">
								<p className="prfs-attribution-label">
									{decision === PlanReviewFeedbackDecision.INCORRECT
										? "Comment (optional)"
										: "What's missing or slightly off? (Optional)"}
								</p>
								<Input.TextArea
									className="prfs-comment"
									rows={3}
									placeholder={"Please explain why the AI analysis is incorrect to help us improve..."}
									value={comment}
									onChange={e => setComment(e.target.value)}
								/>
							</div>
						</>
					)}

					{submitError && <p className="prfs-error">{submitError}</p>}
					<div className="prfs-submit-row">
						<button className="prfs-cancel-button" onClick={resetForm}>
							Cancel
						</button>
						<button className="prfs-submit-button" onClick={handleSubmit} disabled={submitting}>
							{submitting ? "Saving…" : submitLabel}
						</button>
					</div>
				</div>
			)}
		</>
	);

	return (
		<div className="prfs-section" ref={sectionRef}>
			<div className="prfs-header">
				<div className="prfs-header-icon-container">
					<MessageSquare className="prfs-header-icon" />
				</div>
				<div>
					<span className="prfs-header-title">{title}</span>
					<p className="prfs-header-subtitle">{subtitle}</p>
				</div>
			</div>

			{/* Ephemeral: shown only right after a successful save; gone on reopen. */}
			{justSubmitted && (
				<div className="prfs-received-banner">
					<CheckCircle2 className="prfs-received-icon" />
					<div>
						<p className="prfs-received-title">Feedback received</p>
						<p className="prfs-received-subtitle">Thank you for helping Ollie improve!</p>
					</div>
				</div>
			)}

			{/* ── Saved feedback: one card per row, newest first ─────────────────── */}
			{feedbacks.length > 0 && (
				<div className="prfs-feedback-list">
					{feedbacks.map(feedback => (
						<FeedbackCard
							key={feedback.id}
							feedback={feedback}
							canChange={canWriteFeedback && feedback.user_id === currentUserId}
							deleting={deleting}
							editForm={feedback.id === editingFeedbackId ? feedbackForm : null}
							onEdit={() => handleStartEdit(feedback)}
							onDelete={() => handleDelete(feedback.id)}
						/>
					))}
				</div>
			)}

			{/* ── Feedback form (create or edit) ─────────────────────────────────── */}
			{canWriteFeedback && !editingFeedbackId && (!savedFeedback || isEditing) && feedbackForm}
		</div>
	);
};

export default memo(PlanReviewFeedbackSection);
